import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress, TablePagination, Tooltip, IconButton, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { TrendingUp as TotalIcon, AssignmentOutlined as TodoIcon, PlayCircleOutline as OngoingIcon, CheckCircleOutline as CompleteIcon, TimerOffOutlined as OverdueIcon, Visibility as VisibilityIcon, Close as CloseIcon } from "@mui/icons-material";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import { getStatusColor } from "../../../utils/statusColor";
import { formatDate } from "../../../utils/dateFormat";
import Loader from "../../../common/spinner";
import MyProjectDetails from "./myProjectDetails";

function MyProjectList() {
    const [projectData, setProjectData] = useState([]);
    const [statsCounts, setStatsCounts] = useState({
        total_project_count: 0,
        todo_project_count: 0,
        inprogress_count: 0,
        completed_count: 0,
        overdue_count: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [state, updateState] = useState({ page: 0, limit: 10, totalCount: 0 });
    const [selectedProject, setSelectedProject] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userId = userData?.id;

    const handleOpenDetails = (project) => {
        setSelectedProject(project);
        setIsDialogOpen(true);
    };

    const handleCloseDetails = () => {
        setSelectedProject(null);
        setIsDialogOpen(false);
    };

    const fetchProjects = useCallback(async () => {
        if (!userId) return;
        setIsLoading(true);
        try {
            const baseUrl = API_ENDPOINTS.fetchEmployeeProjects;
            const apiPage = state.page + 1;
            const url = `${baseUrl}/${userId}?page=${apiPage}&limit=${state.limit}`;
            const response = await getData(url);

            if (response.status === "SUCCESS") {
                setStatsCounts(response.message || {});
                setProjectData(response.message.data || []);
                updateState(prev => ({ ...prev, totalCount: response.message.total_project_count || 0 }));
            }
        } catch (error) {
            setProjectData([]);
        } finally {
            setTimeout(() => setIsLoading(false), 300);
        }
    }, [state.page, state.limit, userId]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const StatCard = ({ title, value, icon, color }) => (
        <Card
            sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: '100%',
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                color: '#FFFFFF',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                fontWeight: '600',
                                opacity: 0.9
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                            {value || 0}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            p: 1.5,
                            borderRadius: '12px',
                            display: 'flex',
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                    </Box>
                </Stack>
            </CardContent>
            <Box
                sx={{
                    position: 'absolute',
                    top: '-20%',
                    right: '-10%',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: 0
                }}
            />
        </Card>
    );

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><Loader /></Box>;

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1e293b' }}>
                My Projects
            </Typography>

            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard title="All Projects" value={statsCounts.total_project_count} icon={<TotalIcon />} color="#6366F1" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard title="Todo" value={statsCounts.todo_project_count} icon={<TodoIcon />} color="#F59E0B" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard title="Ongoing" value={statsCounts.inprogress_count} icon={<OngoingIcon />} color="#3B82F6" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard title="Completed" value={statsCounts.completed_count} icon={<CompleteIcon />} color="#10B981" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatCard title="Overdue" value={statsCounts.overdue_count} icon={<OverdueIcon />} color="#EF4444" />
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#1E293B' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project Name</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Start Date</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>End Date</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Progress</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#fff' }}>
                            {projectData.length > 0 ? projectData.map((project) => {
                                const statusStyles = getStatusColor(project.status);
                                return (
                                    <TableRow key={project.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{project.project_title}</TableCell>
                                        <TableCell>{formatDate(project.start_date)}</TableCell>
                                        <TableCell>{formatDate(project.end_date)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                {/* Background Circle */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={100}
                                                    size={42}
                                                    thickness={4}
                                                    sx={{ color: '#e2e8f0' }}
                                                />
                                                {/* Active Progress Circle */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={Math.min(project.progress || 0, 100)}
                                                    size={42}
                                                    thickness={4}
                                                    sx={{
                                                        color: project.progress >= 100 ? '#22c55e' : '#6366f1',
                                                        position: 'absolute',
                                                        left: 0,
                                                        [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                    }}
                                                />
                                                {/* Percentage Text Overlay */}
                                                <Box
                                                    sx={{
                                                        top: 0, left: 0, bottom: 0, right: 0,
                                                        position: 'absolute',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                        {`${Math.round(project.progress || 0)}%`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={project.status} size="small" sx={{ width: '100px', fontWeight: 700, borderRadius: '12px', backgroundColor: statusStyles.bg, color: statusStyles.color, border: `1px solid ${statusStyles.color}33` }} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    sx={{ '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' } }}
                                                    onClick={() => handleOpenDetails(project)}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No projects assigned yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <TablePagination
                component="div"
                count={state.totalCount}
                page={state.page}
                onPageChange={(_, newPage) => updateState(prev => ({ ...prev, page: newPage }))}
                rowsPerPage={state.limit}
                onRowsPerPageChange={(e) => updateState(prev => ({ ...prev, limit: parseInt(e.target.value, 10), page: 0 }))}
                rowsPerPageOptions={[5, 10, 25]}
            />

            <Dialog
                open={isDialogOpen}
                onClose={handleCloseDetails}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' }
                }}
            >
                <DialogTitle sx={{
                    m: 0,
                    p: 2,
                    fontWeight: 700,
                    bgcolor: '#F8FAFC',
                    color: '#1e293b',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    Project Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetails}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            '&:hover': { color: '#EF4444' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {selectedProject && (
                        <MyProjectDetails
                            project={selectedProject}
                            onClose={handleCloseDetails}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default MyProjectList;